import { useState, useEffect, useRef } from 'react';
import { Target, Clock, Play, Video, Check, CheckCircle, Loader2, Phone, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useConversation } from '@11labs/react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { processEvaluationGamification } from '@/lib/gamification/evaluationProcessor';
import { formatDisplayContent } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { SessionTimer } from './SessionTimer';

interface InstructionTabProps {
  goalScore: number;
  agentId: string;
  agentName: string;
  agentType: 'patient' | 'customer' | 'examiner' | 'coach';
  userId: string;
  userName: string;
  userEmail: string;
  companyId?: string;
  objectives: string;
  elevenLabsAgentId?: string;
  platform?: string;
  evaluationRubric?: Array<{
    criterion: string;
    description: string;
    weight: number;
  }>;
  onEvaluationSubmitted: () => void;
  agentImageUrl?: string;
  agentInitials?: string;
  agentTitle?: string;
  prepMaterials?: string;
  estimatedDuration?: string;
  showRubricToUsers?: boolean;
  // When provided, called after successful evaluation instead of navigating.
  // Used by the embed route so completion stays in the iframe.
  onSimulationComplete?: (evaluationId: string, score: number) => void;
}

export function InstructionTab({ 
  goalScore, 
  agentId, 
  agentName, 
  agentType,
  userId, 
  userName, 
  userEmail, 
  companyId, 
  objectives,
  elevenLabsAgentId,
  platform,
  evaluationRubric,
  onEvaluationSubmitted,
  agentImageUrl,
  agentInitials,
  agentTitle,
  prepMaterials,
  estimatedDuration,
  showRubricToUsers = true,
  onSimulationComplete
}: InstructionTabProps) {
  const [isSimulating, setIsSimulating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isStarting, setIsStarting] = useState(false); // Prevent double-click race condition
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [maxSessionDuration, setMaxSessionDuration] = useState(agentType === 'coach' ? 3600 : 1200);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const connectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const { hasAnyRole } = useAuth();
  const { tenant } = useTenant();

  // Use refs to avoid stale closure in onDisconnect callback
  const sessionStartTimeRef = useRef<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const evaluationTriggeredRef = useRef(false); // Prevent duplicate webhook calls
  const isProcessingWebhookRef = useRef(false); // Stronger duplicate prevention
  const sessionContainerRef = useRef<HTMLDivElement>(null);
  
  // Store props in refs to avoid stale closures in async callbacks
  const userIdRef = useRef<string>(userId);
  const authUserIdRef = useRef<string | null>(null); // Supabase auth.uid()
  const agentIdRef = useRef<string>(agentId);
  const agentNameRef = useRef<string>(agentName);
  const agentTypeRef = useRef<'patient' | 'customer' | 'examiner' | 'coach'>(agentType);
  const userNameRef = useRef<string>(userName);
  const userEmailRef = useRef<string>(userEmail);
  const companyIdRef = useRef<string | undefined>(companyId);
  const goalScoreRef = useRef<number>(goalScore);
  const agentDataRef = useRef<any>(null); // Full agent data for evaluation knowledge sources

  // Auto-scroll to session area when simulation starts
  useEffect(() => {
    if (isSimulating && sessionContainerRef.current) {
      sessionContainerRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [isSimulating]);

  const conversation = useConversation({
    onConnect: () => {
      // Clear connection timeout on successful connection
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      setConnectionError(null);
      toast.success('Connected! Start speaking with the agent.');
    },
    onDisconnect: () => {
      handleSimulationComplete();
    },
    onError: (error) => {
      console.error('❌ ElevenLabs error:', error);
      // Clear connection timeout on error
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      toast.error('Conversation error. Please try again.');
      setIsSimulating(false);
      setIsStarting(false);
      setConnectionError('A connection error occurred. Please try again.');
    }
  });

  // Use custom rubric if available, otherwise fall back to default criteria
  const evaluationCriteria = evaluationRubric && evaluationRubric.length > 0
    ? evaluationRubric.map(item => ({
        name: item.criterion,
        points: item.weight,
        description: item.description
      }))
    : [
        // Default fallback criteria if no custom rubric is configured
        { name: 'Product Knowledge & Clinical Understanding', points: 20 },
        { name: 'Communication & Presentation Skills', points: 20 },
        { name: 'Objection Handling', points: 20 },
        { name: 'Relationship Building', points: 20 },
        { name: 'Closing & Follow-up Strategy', points: 20 },
      ];

  const handleStartSimulation = async () => {
    // Prevent double-clicks - return immediately if already starting
    if (isStarting) {
      return;
    }
    setIsStarting(true);
    
    try {
      // Check if this is a coaching session
      if (agentType === 'coach') {
        // Route based on platform: Anam video or ElevenLabs audio
        if (platform === 'anam') {
          navigate(`/coaching/${agentId}/session`);
          return;
        }
        // For ElevenLabs audio coaching, continue with audio flow below
      }

      // Check if this is an Anam video simulation (non-coach)
      if (platform === 'anam' && agentType !== 'coach') {
        navigate(`/simulation/${agentId}/video`);
        return;
      }

      // Audio simulation flow
      // Check if voice agent is configured
      if (!elevenLabsAgentId) {
        toast.error('This simulation agent is not configured for audio. Please contact your administrator.');
        console.error('❌ No voice agent ID configured for agent:', agentId);
        return;
      }

      // Request microphone permissions
      await navigator.mediaDevices.getUserMedia({ audio: true });
      
      toast.info('Preparing conversation...');
      
      
      const { data: signedUrlData, error: signedUrlError } = await supabase.functions.invoke(
        'elevenlabs-get-signed-url',
        {
          body: { agentId: elevenLabsAgentId }
        }
      );

      if (signedUrlError || !signedUrlData?.signedUrl) {
        console.error('❌ Failed to get signed URL:', signedUrlError);
        toast.error('Failed to initialize conversation. Please try again or contact support.');
        return;
      }

      
      // Fetch FULL agent data to build custom system prompt
      const { data: agentData, error: agentError } = await supabase
        .from('simulation_agents')
        .select('*')
        .eq('id', agentId)
        .maybeSingle();
      
      if (agentError || !agentData) {
        console.error('❌ Failed to fetch agent data:', agentError);
        toast.error('Failed to load agent configuration.');
        return;
      }
      
      // Store agent data in ref for later use in handleSimulationComplete
      agentDataRef.current = agentData;

      // Warn admins if coach agent has no output template assigned
      if (agentType === 'coach' && !agentData.coaching_template_id && hasAnyRole(['admin', 'company_admin', 'tenant_admin'])) {
        toast.warning('This coach agent has no output template assigned. The session will proceed but no document will be generated.');
      }

      // Set max session duration from agent config (state for timer display)
      const duration = agentData.max_session_duration_seconds || (agentType === 'coach' ? 3600 : 1200);
      setMaxSessionDuration(duration);

      // Load configurable tone/disclosure prompts from system_settings (if available)
      // Wrapped in try-catch because regular users may not have RLS access to system_settings
      try {
        if (agentData.conversation_tone) {
          const { data: toneSetting } = await (supabase as any)
            .from('system_settings')
            .select('setting_value')
            .eq('setting_key', `tone_${agentData.conversation_tone}`)
            .maybeSingle();
          if (toneSetting?.setting_value?.prompt) {
            (agentData as any)._tonePromptOverride = toneSetting.setting_value.prompt;
          }
        }
        if (agentData.disclosure_style) {
          const { data: disclosureSetting } = await (supabase as any)
            .from('system_settings')
            .select('setting_value')
            .eq('setting_key', `disclosure_${agentData.disclosure_style}`)
            .maybeSingle();
          if (disclosureSetting?.setting_value?.prompt) {
            (agentData as any)._disclosurePromptOverride = disclosureSetting.setting_value.prompt;
          }
        }
      } catch {
        // Non-critical: fall back to built-in defaults if system_settings is not accessible
      }

      // Import prompt builders (dynamic import to avoid circular deps)
      const { buildRuntimePrompt, buildRuntimeFirstMessage } = await import('@/lib/prompt/runtime');

      // V2 compiler only for explicitly converted agents (prompt_schema_version === 2);
      // every other agent still goes through buildSystemPrompt, unchanged.
      const systemPrompt = buildRuntimePrompt(agentData as any);
      const firstMessage = buildRuntimeFirstMessage(agentData as any);
      
      
      // Warn if first_message field is empty for non-coach agents where it matters
      if (!agentData.first_message && agentData.type !== 'coach') {
        console.warn('⚠️ Agent has no custom first_message configured - using type-based fallback');
      }
      
      // Start session tracking
      sessionStartTimeRef.current = new Date().toISOString();
      conversationIdRef.current = null;
      evaluationTriggeredRef.current = false; // Reset for new session
      isProcessingWebhookRef.current = false; // Reset for new session
      
      // Get Supabase auth user ID for webhook (required for RLS)
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        toast.error('You must be logged in to start a simulation');
        return;
      }
      authUserIdRef.current = authUser.id;
      
      // Capture props in refs at session start to avoid stale closures
      userIdRef.current = userId;
      agentIdRef.current = agentId;
      agentNameRef.current = agentName;
      agentTypeRef.current = agentType;
      userNameRef.current = userName;
      userEmailRef.current = userEmail;
      companyIdRef.current = companyId;
      goalScoreRef.current = goalScore;
      
      
      // Set session start time for timer display
      setSessionStartTime(new Date());
      
      // Start ElevenLabs conversation with custom prompt override
      // Note: max_conversation_duration must be configured in ElevenLabs dashboard (Agent > Advanced settings)
      // The SessionTimer component handles client-side time display and warnings
      
      // Set connection timeout BEFORE awaiting startSession
      // Timeout is ONLY cleared by onConnect callback (not by startSession resolving)
      // because startSession resolves before audio/WebRTC is actually connected
      connectionTimeoutRef.current = setTimeout(() => {
        console.error('❌ Connection timeout after 15 seconds');
        setIsSimulating(false);
        setIsStarting(false);
        setConnectionError('Unable to connect. Please check your microphone permissions and try again.');
        try {
          conversation.endSession();
        } catch (e) {
          // Ignore if session wasn't fully started
        }
      }, 15000);
      
      const convId = await conversation.startSession({
        signedUrl: signedUrlData.signedUrl,
        overrides: {
          agent: {
            prompt: {
              prompt: systemPrompt
            },
            firstMessage: firstMessage
          }
        }
      });
      
      
      conversationIdRef.current = convId;
      setIsSimulating(true);
      
      
    } catch (error) {
      console.error('❌ Failed to start simulation:', error);
      setIsStarting(false); // Reset on error so user can try again
      setConnectionError('Unable to connect. Please check your microphone permissions and try again.');
    }
  };

  // Returns transcript if conversation is ready, null if failed/timeout
  const pollForConversationReady = async (conversationId: string, maxAttempts = 30): Promise<{ ready: boolean; transcript: string | null }> => {
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      
      try {
        const { data, error } = await supabase.functions.invoke(
          'elevenlabs-check-conversation-status',
          {
            body: { conversationId }
          }
        );

        if (error) {
          console.error('Error checking status:', error);
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }

        if (data.status === 'done') {
          return { ready: true, transcript: data.transcript || null };
        }

        if (data.status === 'failed') {
          console.error('❌ Conversation processing failed');
          return { ready: false, transcript: null };
        }

        // Status is still "processing" or "in-progress", wait and retry
        await new Promise(resolve => setTimeout(resolve, 2000));
        
      } catch (error) {
        console.error('Polling error:', error);
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    
    console.error('❌ Conversation did not complete processing in time');
    return { ready: false, transcript: null };
  };

  // Generate coaching document via edge function
  const generateCoachingDocument = async (sessionId: string): Promise<boolean> => {

    try {
      const { data, error } = await supabase.functions.invoke('generate-coaching-document', {
        body: { sessionId }
      });

      if (error) {
        console.error('❌ Edge function failed:', error);
        return false;
      }

      if (data?.success) {
        return true;
      }

      console.error('❌ Edge function returned unsuccessful:', data?.error);
      return false;
    } catch (err) {
      console.error('❌ Edge function error:', err);
      return false;
    }
  };

  // Evaluate simulation via edge function
  const evaluateSimulation = async (sessionData: Record<string, any>): Promise<{ success: boolean; evaluationId?: string; error?: string }> => {

    try {
      const { data, error } = await supabase.functions.invoke('evaluate-simulation', {
        body: sessionData
      });

      if (error) {
        console.error('❌ Evaluation edge function failed:', error);
        return { success: false, error: error.message };
      }

      if (data?.success) {
        return { success: true, evaluationId: data.evaluation_id };
      }

      console.error('❌ Evaluation returned unsuccessful:', data?.error);
      return { success: false, error: data?.error || 'Evaluation failed' };
    } catch (err) {
      console.error('❌ Evaluation error:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  };

  // Create coaching session record before generating document
  const createCoachingSessionRecord = async (sessionId: string, transcript?: string): Promise<boolean> => {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return false;

    const { error } = await supabase
      .from('coaching_sessions')
      .insert({
        session_id: sessionId,
        tenant_id: agentDataRef.current?.tenant_id ?? tenant.tenant_id,
        agent_id: agentIdRef.current,
        template_id: agentDataRef.current?.coaching_template_id || null,
        user_id: userIdRef.current,
        auth_user_id: authUser.id,
        company_id: companyIdRef.current || null,
        status: 'generating',
        session_start: sessionStartTimeRef.current,
        platform: 'elevenlabs',
        // Store transcript so edge function can access it
        conversation_transcript: transcript ? [{ role: 'transcript', content: transcript }] : null
      });

    if (error) {
      console.error('❌ Failed to create coaching session record:', error);
      return false;
    }

    return true;
  };

  const handleSimulationComplete = async () => {
    // Duplicate prevention - check BOTH flags immediately
    if (evaluationTriggeredRef.current || isProcessingWebhookRef.current) {
      return;
    }
    // Mark as processing IMMEDIATELY before any async work
    isProcessingWebhookRef.current = true;
    evaluationTriggeredRef.current = true;

    // Transition to processing state
    setIsSimulating(false);
    setIsProcessing(true);

    // Validate we have required data from refs (not props which may be stale)
    if (!conversationIdRef.current || !sessionStartTimeRef.current) {
      console.error('❌ Missing session data - cannot trigger evaluation');
      toast.error('Missing session data. Please contact support.');
      setIsProcessing(false);
      isProcessingWebhookRef.current = false;
      return;
    }

    // Validate user data captured at session start
    if (!userIdRef.current || !agentIdRef.current || !authUserIdRef.current) {
      console.error('❌ CRITICAL: Missing required data for evaluation!', {
        userId: userIdRef.current,
        agentId: agentIdRef.current,
        authUserId: authUserIdRef.current
      });
      toast.error('Session data missing. Please try again.');
      setIsProcessing(false);
      isProcessingWebhookRef.current = false;
      return;
    }

    // Show processing message
    toast.info('Processing conversation transcript...');

    // Wait for ElevenLabs to finish processing AND get transcript
    const { ready: isReady, transcript: fetchedTranscript } = await pollForConversationReady(conversationIdRef.current);

    if (!isReady) {
      toast.error('Conversation processing failed or timed out. Please contact support.');
      setIsProcessing(false);
      isProcessingWebhookRef.current = false;
      return;
    }

    // USE REFS (captured at session start) to avoid stale closures
    const isCoaching = agentTypeRef.current === 'coach';
    const sessionId = `${userIdRef.current}_${agentIdRef.current}_${Date.now()}`;

    const sessionData: Record<string, any> = {
      session_id: sessionId,
      simulation_id: agentIdRef.current,
      agent_id: agentIdRef.current,
      agent_name: agentNameRef.current,
      agent_type: agentTypeRef.current,
      user_id: userIdRef.current,
      auth_user_id: authUserIdRef.current,
      user_name: userNameRef.current,
      user_email: userEmailRef.current,
      company_id: companyIdRef.current,
      platform: 'elevenlabs',
      conversation_id: conversationIdRef.current,
      conversation_transcript: fetchedTranscript || 'No transcript available',
      evaluation_criteria: objectives,
      evaluation_knowledge_sources: agentDataRef.current?.evaluation_knowledge_sources || [],
      knowledge_sources: agentDataRef.current?.knowledge_sources || [],
      goal_score: goalScoreRef.current || 80,
      session_start: sessionStartTimeRef.current,
      session_end: new Date().toISOString(),
      include_conversation_references: true
    };

    try {
      if (isCoaching) {
        // COACHING FLOW: Create session record, then generate document via edge function
        const recordCreated = await createCoachingSessionRecord(sessionId, fetchedTranscript || undefined);
        if (!recordCreated) {
          toast.error('Failed to create coaching session. Please try again.');
          setIsProcessing(false);
          isProcessingWebhookRef.current = false;
          return;
        }

        toast.info('Generating your coaching document...');

        const documentGenerated = await generateCoachingDocument(sessionId);

        if (documentGenerated) {
          navigate(`/coaching/results/${sessionId}`);
        } else {
          toast.error('Document generation failed. Check your coaching history.');
          navigate('/coaching/history');
        }
      } else {
        // SIMULATION FLOW: Evaluate via edge function (creates evaluation record directly)
        toast.info('Evaluating your performance...');

        const result = await evaluateSimulation(sessionData);

        if (result.success && result.evaluationId) {
          // Fetch evaluation data to get the score for gamification
          const { data: evalData } = await supabase
            .from('simulation_evaluations')
            .select('overall_score')
            .eq('id', result.evaluationId)
            .single();

          // Process gamification immediately after evaluation
          if (evalData) {
            await processEvaluationGamification(
              userId,
              result.evaluationId,
              agentId,
              agentName,
              evalData.overall_score || 0,
              goalScore
            );
          }

          if (onSimulationComplete) {
            // Embed mode: stay in iframe, let parent handle completion
            onSimulationComplete(result.evaluationId, evalData?.overall_score || 0);
          } else {
            navigate(`/simulation/results/${result.evaluationId}`);
          }
        } else {
          toast.error(result.error || 'Evaluation failed. Please try again.');
          onEvaluationSubmitted();
        }
      }

    } catch (error) {
      console.error('❌ Failed to process evaluation:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to submit for evaluation. Please contact support.');
      setIsProcessing(false);
    } finally {
      sessionStartTimeRef.current = null;
      conversationIdRef.current = null;
      setSessionStartTime(null);
      // Note: evaluationTriggeredRef stays true - only reset at next session START
      // isProcessingWebhookRef stays true - only reset at next session START
    }
  };

  const handleEndSession = () => {
    conversation.endSession();
  };

  return (
    <div className="space-y-8">
      {/* Background And Instructions Section - if available */}
      {prepMaterials && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              Background And Instructions
            </CardTitle>
            <CardDescription>
              Review these materials before starting the simulation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div 
              className="rich-content-display max-w-none"
              dangerouslySetInnerHTML={{ __html: formatDisplayContent(prepMaterials) }}
            />
          </CardContent>
        </Card>
      )}

      {/* Objective Box */}
      <div className="bg-primary-subtle border-l-4 border-primary rounded-lg p-6">
        <h2 className="text-2xl font-bold text-foreground mb-3">Simulation Objective</h2>
        <div 
          className="rich-content-display max-w-none"
          dangerouslySetInnerHTML={{ 
            __html: formatDisplayContent(objectives) || `Conduct a professional consultation demonstrating clinical expertise, 
            effective communication, and relationship building. 
            Your goal is to present your value proposition while addressing concerns 
            and building trust.`
          }}
        />
      </div>

      {/* Evaluation Criteria - Only show if showRubricToUsers is true */}
      {showRubricToUsers && (
        <div>
          <h3 className="text-xl font-bold text-foreground mb-4">Evaluation Criteria</h3>
          <div className="grid gap-4">
            {evaluationCriteria.map((criterion) => (
              <div key={criterion.name} className="flex items-start gap-3 p-4 bg-card rounded-lg border border-border">
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
                  <Check size={16} className="text-primary-foreground" />
                </div>
                <div className="flex-1">
                  <span className="text-foreground font-medium block">{criterion.name}</span>
                  {criterion.description && (
                    <span className="text-sm text-muted-foreground mt-1 block">{criterion.description}</span>
                  )}
                </div>
                <span className="text-primary font-bold whitespace-nowrap">{criterion.points} points</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Duration */}
      <div className="flex items-center gap-3 text-muted-foreground">
        <Clock size={20} />
        <span>Approximately {estimatedDuration || '15-20'} minutes</span>
      </div>

      {/* Connection Error State */}
      {connectionError && !isSimulating && !isProcessing && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-6 text-center space-y-4">
          <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center mx-auto">
            <Phone className="text-destructive h-8 w-8" />
          </div>
          <h3 className="text-lg font-semibold text-destructive">Connection Failed</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">{connectionError}</p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => {
                setConnectionError(null);
                setIsStarting(false);
                navigate(-1);
              }}
              variant="outline"
              size="lg"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConnectionError(null);
                setIsStarting(false);
                // Slight delay to allow state to reset before re-triggering
                setTimeout(() => handleStartSimulation(), 100);
              }}
              className="bg-primary hover:bg-primary-light text-primary-foreground"
              size="lg"
            >
              <Play size={20} />
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* Start Button */}
      {!isSimulating && !isProcessing && !connectionError && (
        <Button
          onClick={handleStartSimulation}
          disabled={isStarting}
          className="w-full bg-primary hover:bg-primary-light text-primary-foreground font-semibold py-6 text-lg"
          size="lg"
        >
          {isStarting ? (
            <>
              <Loader2 className="animate-spin" size={24} />
              Connecting...
            </>
          ) : (
            <>
              <Play size={24} />
              Start Simulation
            </>
          )}
        </Button>
      )}

      {/* ElevenLabs Conversation Interface */}
      {isSimulating && (
        <div 
          ref={sessionContainerRef}
          className="bg-card border-2 border-primary rounded-xl p-12 text-center" 
          style={{minHeight: '720px'}}
        >
          <div className="flex flex-col items-center justify-center h-full space-y-6">
            
            {/* Live Session Indicator */}
            <div className="flex items-center gap-3 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-full">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-red-600 dark:text-red-400">Live Session</span>
            </div>
            
            {/* Session Timer */}
            <SessionTimer
              maxDurationSeconds={maxSessionDuration}
              startTime={sessionStartTime}
              isActive={isSimulating}
              onTimeExpired={handleEndSession}
            />
            
            {/* Agent Avatar with Pulse Ring Animation */}
            <div className="relative">
              {/* Animated rings */}
              <div className="absolute inset-0 w-40 h-40 rounded-full border-4 border-primary/30 animate-ping" style={{animationDuration: '2s'}} />
              <div className="absolute inset-0 w-40 h-40 rounded-full border-2 border-primary/20 animate-pulse" />
              
              {/* Agent Avatar */}
              <Avatar className="relative w-40 h-40 border-4 border-primary shadow-xl">
                <AvatarImage src={agentImageUrl} alt={agentName} />
                <AvatarFallback 
                  className="text-4xl font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, hsl(220, 70%, 60%), hsl(280, 70%, 70%))' }}
                >
                  {agentInitials}
                </AvatarFallback>
              </Avatar>
            </div>
            
            {/* Session Header */}
            <div className="space-y-2">
              <h2 className="text-3xl font-bold text-foreground">
                Your Session with {agentName}
              </h2>
              {agentTitle && (
                <p className="text-lg text-muted-foreground">{agentTitle}</p>
              )}
            </div>
            
            {/* Instructions */}
            <p className="text-muted-foreground max-w-md">
              Speak clearly and naturally. The agent will respond to your conversation in real-time.
            </p>
            
            {/* End Session Button */}
            <Button
              variant="destructive"
              size="lg"
              onClick={handleEndSession}
              className="mt-4 px-8 py-6 text-lg"
            >
              <Phone className="mr-2 h-5 w-5 rotate-[135deg]" />
              End Session
            </Button>
            
          </div>
        </div>
      )}

      {/* Processing State - Thank You & Progress */}
      {!isSimulating && isProcessing && (
        <div className="bg-card border-2 border-primary rounded-xl p-12 text-center" style={{minHeight: '720px'}}>
          <div className="flex flex-col items-center justify-center h-full space-y-6">
            {/* Success Icon */}
            <div className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-2">
              <CheckCircle size={56} className="text-green-600 dark:text-green-400" />
            </div>
            
            {/* Thank You Header */}
            <div className="space-y-2">
              <h2 className="text-3xl font-bold text-foreground">
                Session Complete
              </h2>
              <p className="text-lg text-muted-foreground">
                Thank you for completing your simulation with <span className="font-semibold text-foreground">{agentName}</span>
              </p>
            </div>
            
            {/* Processing Section */}
            <div className="w-full max-w-md space-y-4 pt-4">
              <div className="flex items-center justify-center gap-2 text-primary">
                <Loader2 className="animate-spin" size={20} />
                <span className="font-medium">Analyzing your performance...</span>
              </div>
              
              {/* Progress Bar with Animation */}
              <div className="relative h-2 w-full bg-muted rounded-full overflow-hidden">
                <div className="absolute h-full w-1/3 bg-primary rounded-full animate-progress" />
              </div>
              
              <p className="text-sm text-muted-foreground">
                Your conversation is being evaluated. This typically takes 15-30 seconds.
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
